import React, { useMemo, useState, useEffect } from 'react'
import { ArrowRight, Phone, Mail, MapPin } from 'lucide-react'
import { useSiteData } from '../context/SiteDataContext.jsx'
import { focusPages } from '../data/navigation.js'
import { excerptFromHtml, getCmsPageText, optimizeAssetUrl } from '../lib/cms.js'
import { Container } from '../components/atoms.jsx'
import { SeoManager, StoryMarquee } from '../components/content.jsx'

const weTreatList = [
  { label: 'Akillessenen – smerter i akillessenen', href: '/sygdomme-lidelser/akillessen-smerter-i-akillessenen' },
  { label: 'Akupunktur', href: '/akupunktur' },
  { label: 'Albue smerter – tennisalbue, golfalbue eller musearm', href: '/sygdomme-lidelser/albue-smerter-tennisalbue-golfalbue-eller-musearm' },
  { label: 'Astma – hoste', href: '/sygdomme-lidelser/astma-hoste' },
  { label: 'Barnløshed / fertilitetsproblemer', href: '/sygdomme-lidelser/barnloeshed-fertilitetsproblemer' },
  { label: 'Bihule- og kæbebetændelse (Sinuitis)', href: '/sygdomme-lidelser/bihule-og-kaebebetaendelse-sinuitis' },
  { label: 'Diskusprolaps', href: '/sygdomme-lidelser/diskusprolaps' },
  { label: 'Eksem – hudlidelser', href: '/sygdomme-lidelser/eksem-hudlidelser' },
  { label: 'Fibersprængning', href: '/sygdomme-lidelser/fiberspraengning' },
  { label: 'Fod og ankelproblemer', href: '/sygdomme-lidelser/fod-og-ankel-problemer' },
  { label: 'Frossen skulder – skuldersmerter', href: '/sygdomme-lidelser/frossen-skulder-skuldersmerter' },
  { label: 'Herpes Simplex (forkølelsessår)', href: '/sygdomme-lidelser/herpes-simplex-forkoelelsessaar' },
  { label: 'Herpes Zoster Helvedesild', href: '/sygdomme-lidelser/herpes-zoster-helvedesild' },
  { label: 'Hofte smerter', href: '/sygdomme-lidelser/hofte-smerter' },
  { label: 'Hovedpine – migræne og Hortons hovedpine', href: '/sygdomme-lidelser/hovedpine-migraene-og-hortons-hovepine' },
  { label: 'Hælspore', href: '/sygdomme-lidelser/haelspore' },
  { label: 'Høfeber / pollenallergi (sæsonbetonet allergi)', href: '/sygdomme-lidelser/hoefeber-pollenallergi-saesonbetonet-allergi' },
  { label: 'Iskias smerter', href: '/sygdomme-lidelser/iskias-smerter' },
  { label: 'Knæ smerter', href: '/sygdomme-lidelser/knae-smerter' },
  { label: 'KOL', href: '/kol' },
  { label: 'Korsbåndsskader i knæet', href: '/sygdomme-lidelser/korsbaand-skader-i-knaeet' },
  { label: 'Kræftbehandling', href: '/sygdomme-lidelser/kraeftbehandling-bivirkninger-fra-kemo-og-straalebehandling' },
  { label: 'Kvalme', href: '/sygdomme-lidelser/kvalme' },
  { label: 'Ledbåndsskader', href: '/sygdomme-lidelser/ledbaandsskader-i-knaeet' },
  { label: 'Leddegigt', href: '/sygdomme-lidelser/leddegigt' },
  { label: 'Mellemørebetændelse', href: '/sygdomme-lidelser/mellemoerebetaendelse' },
  { label: 'Meniskskader', href: '/sygdomme-lidelser/meniskskader' },
  { label: 'Menstruations problemer', href: '/sygdomme-lidelser/menstruations-problemer' },
  { label: 'Nedsunken forfod', href: '/sygdomme-lidelser/nedsunken-forfod' },
  { label: 'Osgood Schlatters Syndrom', href: '/sygdomme-lidelser/osgood-schlatters-syndrom' },
  { label: 'Overgangsalder Hedeture', href: '/sygdomme-lidelser/overgangsalder-hedeture' },
  { label: 'Piskesmæld', href: '/sygdomme-lidelser/piskesmaeld-whiplash' },
  { label: 'Rygestop', href: '/sygdomme-lidelser/rygestop-tobaksafvaenning' },
  { label: 'Rygsmerter', href: '/rygsmerter' },
  { label: 'Slidgigt', href: '/sygdomme-lidelser/slidgigt-i-knaeet-en-kronisk-degenerativ-sygdom' },
  { label: 'Springerknæ', href: '/sygdomme-lidelser/springerknae-jumpers-knee' },
  { label: 'Sportsskader', href: '/sportsskader' },
  { label: 'Stress', href: '/sygdomme-lidelser/stress' },
  { label: 'Søvnløshed', href: '/sygdomme-lidelser/soevnloeshed-insomania' },
  { label: 'Tinnitus', href: '/sygdomme-lidelser/tinnitus-oeresusen' },
]

const behandlingsmetoderList = [
  { label: 'Traditionel Kinesisk medicin (T.K.M.)', slug: 'traditionel-kinesisk-medicin-t-k-m' },
  { label: 'Klassisk Kinesisk akupunktur nær Århus', slug: 'akupunktur' },
  { label: 'Kinesisk urtemedicin', slug: 'kinesisk-urtemedicin' },
  { label: 'Chi-Kung (energitræning)', slug: 'chi-kung-energitraening' },
  { label: 'Chi-Kung workshop', slug: 'chi-kung-workshop' },
  { label: 'Tai Chi Chuan', slug: 'tai-chi-chuan' },
  { label: 'Øreakupunktur', slug: 'oereakupunktur' },
  { label: 'Posturologi', slug: 'posturologi' },
  { label: 'Laserterapi', slug: 'laserterapi' },
  { label: 'Magnetfelt Terapi', slug: 'magnetfelt-terapi' },
  { label: 'TENS', slug: 'tens' },
  { label: 'E.F.T. – Banke Akupunktur', slug: 'e-f-t-banke-akupunktur' },
  { label: 'Vægttab på den nemme måde', slug: 'vaegttab-paa-den-nemme-maade' },
]

const slides = [
  {
    text: "Klassisk kinesisk akupunktur & naturlig sundhed",
    image: "https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/helseklinikken.png"
  },
  {
    text: "Vi er blandt landets mest erfarne akupunktører",
    image: "https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/akupunktur-1.jpg"
  },
  {
    text: "Vi behandler alle aldre, små og store",
    image: "https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/barn.png"
  },
  {
    text: "Vejen til energi og bedre helbred, balance mellem krop og sind",
    image: "https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/skov.png"
  },
  {
    text: "Chi Kung Workshop i skoven",
    image: "https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/imgi-23-chikung2.png"
  },
  {
    text: "Vi er instruktører i Zhan Zhuang Chi Kung",
    image: "https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/chikung1.png"
  }
]

export function HomePage() {
  const { getPage, patienthistorier, pages } = useSiteData()
  const [currentSlide, setCurrentSlide] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length)
    }, 4000)
    return () => clearInterval(interval)
  }, [])

  const home = getPage('index')

  const heroDescription =
    'Døjer du med rygsmerter eller har brug for akupunktur på Djursland nær Århus? Helseklinikken kan hjælpe dig. Kontakt os i dag og book tid'

  const featuredStories = useMemo(
    () => (patienthistorier.length > 0 ? patienthistorier.slice(0, 15) : []),
    [patienthistorier],
  )

  // Split weTreatList into 4 equal columns for the design reference layout
  const colSize = Math.ceil(weTreatList.length / 4)
  const col1 = weTreatList.slice(0, colSize)
  const col2 = weTreatList.slice(colSize, colSize * 2)
  const col3 = weTreatList.slice(colSize * 2, colSize * 3)
  const col4 = weTreatList.slice(colSize * 3)
  const weTreatColumns = [col1, col2, col3, col4]

  return (
    <>
      <SeoManager
        title={home?.meta_title || 'Akupunktur på Djursland nær Århus | Rygsmerter | Helseklinikken'}
        description={home?.meta_description || heroDescription}
        image={home?.image?.[0] || 'https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/557636-305391606197576-942530587-n.jpg'}
        canonical="https://helselarsen.dk/"
      />

      <main className="bg-white">
        
        {/* 1. Hero Section: Slider (Left) & Facebook Widget (Right) */}
        <section className="relative overflow-hidden bg-white border-b border-stone-200">
          <div className="grid grid-cols-1 lg:grid-cols-12 w-full min-h-[400px] lg:min-h-[500px]">
            
            {/* Left Column (8/12 width) - Image Slider */}
            <div className="lg:col-span-8 relative min-h-[320px] lg:min-h-[500px] overflow-hidden bg-stone-100">
              {slides.map((slide, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                    index === currentSlide ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <img
                    src={`https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/${slide.image.replace('https://assets.auxodev.dk/', '')}`}
                    alt={slide.text}
                    className="w-full h-full object-cover"
                  />
                  {/* Text overlay centered on the slider */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/25 to-black/10 p-6 md:p-8 text-white flex flex-col justify-center items-center text-center">
                    <div className="max-w-2xl mx-auto space-y-2.5">
                      <p className="text-sm sm:text-base md:text-lg font-medium text-stone-100">
                        Vi tilbyder behandling, workshops og foredrag
                      </p>
                      <p className="text-base sm:text-xl md:text-2xl font-bold text-white pt-1 drop-shadow-sm">
                        {slide.text}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Right Column (4/12 width) - Green Facebook Box */}
            <div className="lg:col-span-4 bg-forest-600 flex flex-col justify-center items-center p-6 sm:p-8 min-h-[400px] lg:min-h-[500px]">
              <div className="w-full max-w-[340px] h-[450px] overflow-hidden rounded-2xl bg-white shadow-md p-1">
                <iframe 
                  src="https://www.facebook.com/plugins/page.php?href=https%3A%2F%2Fwww.facebook.com%2Fprofile.php%3Fid%3D100057487268744&show_posts=true&width=340&height=450&small_header=true&adapt_container_width=true&hide_cover=true&show_facepile=false&appId" 
                  width="100%" 
                  height="450" 
                  style={{ border: 'none', overflow: 'hidden' }} 
                  scrolling="no" 
                  frameBorder="0" 
                  allowFullScreen={true} 
                  allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                  title="Facebook Feed"
                  className="w-full h-full"
                ></iframe>
              </div>
            </div>

          </div>
        </section>

        {/* 2. Triple Promo Blocks (Solid Green Gradient with Images) */}
        <section className="grid grid-cols-1 md:grid-cols-3 w-full">
          
          {/* Block 1: Behandlingsmetoder */}
          <a
            href="/behandlingsmetoder"
            className="relative overflow-hidden group flex flex-col justify-between p-8 md:p-10 text-white min-h-[380px] md:min-h-[420px] bg-forest-950"
          >
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/shutterstock-1565674588.jpg"
              alt="Behandlingsmetoder"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-black/10 to-transparent transition-opacity duration-300" />
            <div className="absolute inset-0 bg-gradient-to-t from-forest-950/95 via-forest-900/90 to-forest-800/85 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            
            <div className="relative z-10 flex-1 flex flex-col justify-between drop-shadow-sm">
              <div className="space-y-4">
                <div className="text-white/95 text-4xl">
                  <i className="fa-light fa-spa" />
                </div>
                <h3 className="text-lg md:text-xl font-bold tracking-wide uppercase">
                  Behandlingsmetoder
                </h3>
                <p className="text-xs md:text-sm text-white/90 leading-relaxed [text-shadow:0_1px_3px_rgba(0,0,0,0.6)]">
                  Vi tilbyder en bred vifte af behandlingsmetoder tilpasset dine behov, herunder akupunktur, kinesisk urtemedicin, laserterapi og magnetfeltterapi.
                </p>
              </div>
              <div className="pt-6 border-t border-white/20 mt-6 flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider group-hover:underline">
                  Læs mere her
                </span>
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </div>
            </div>
          </a>

          {/* Block 2: Sygdomme / lidelser */}
          <a
            href="/sygdomme-lidelser"
            className="relative overflow-hidden group flex flex-col justify-between p-8 md:p-10 text-white min-h-[380px] md:min-h-[420px] bg-forest-950"
          >
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/shutterstock-2468776477.jpg"
              alt="Sygdomme og lidelser"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-black/10 to-transparent transition-opacity duration-300" />
            <div className="absolute inset-0 bg-gradient-to-t from-forest-950/95 via-forest-900/90 to-forest-800/85 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            
            <div className="relative z-10 flex-1 flex flex-col justify-between drop-shadow-sm">
              <div className="space-y-4">
                <div className="text-white/95 text-4xl">
                  <i className="fa-light fa-heart-pulse" />
                </div>
                <h3 className="text-lg md:text-xl font-bold tracking-wide uppercase">
                  Sygdomme / lidelser
                </h3>
                <p className="text-xs md:text-sm text-white/90 leading-relaxed [text-shadow:0_1px_3px_rgba(0,0,0,0.6)]">
                  Vi behandler mange forskellige sygdomme og lidelser. Se listen over de mest almindelige tilstande, vi kan hjælpe dig med.
                </p>
              </div>
              <div className="pt-6 border-t border-white/20 mt-6 flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider group-hover:underline">
                  Læs mere her
                </span>
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </div>
            </div>
          </a>

          {/* Block 3: Priser */}
          <a
            href="/priser"
            className="relative overflow-hidden group flex flex-col justify-between p-8 md:p-10 text-white min-h-[380px] md:min-h-[420px] bg-forest-950"
          >
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/priser.jpg"
              alt="Priser"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-black/10 to-transparent transition-opacity duration-300" />
            <div className="absolute inset-0 bg-gradient-to-t from-forest-950/95 via-forest-900/90 to-forest-800/85 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            
            <div className="relative z-10 flex-1 flex flex-col justify-between drop-shadow-sm">
              <div className="space-y-4">
                <div className="text-white/95 text-4xl">
                  <i className="fa-light fa-tag" />
                </div>
                <h3 className="text-lg md:text-xl font-bold tracking-wide uppercase">
                  Priser
                </h3>
                <p className="text-xs md:text-sm text-white/90 leading-relaxed [text-shadow:0_1px_3px_rgba(0,0,0,0.6)]">
                  Se vores priser på akupunktur og andre behandlinger. Her finder du også information om tilskud fra Sygeforsikringen "danmark" og forsikringsselskaber.
                </p>
              </div>
              <div className="pt-6 border-t border-white/20 mt-6 flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider group-hover:underline">
                  Se priser her
                </span>
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </div>
            </div>
          </a>

        </section>

        {/* 2. Introduction Section ("Velkommen") */}
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="font-sans text-2xl md:text-3xl font-bold text-stone-900 text-center mb-12 tracking-tight">
              Velkommen til Helseklinikken i Gjerrild
            </h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              
              {/* Left Column */}
              <div className="space-y-8">
                <div className="space-y-4">
                  <h3 className="font-sans text-xl md:text-2xl font-bold text-stone-900 leading-tight">
                    Vi tilbyder akupunktur mod rygsmerter på Djursland nær Århus
                  </h3>
                  <h4 className="text-sm font-semibold text-forest-700 leading-relaxed uppercase tracking-wider">
                    Vi kan hjælpe dig med dine rygsmerter og anvender akupunktur
                  </h4>
                  <div className="prose prose-stone text-sm text-stone-600 leading-relaxed space-y-4">
                    <p>
                      Har du rygsmerter og har brug for akupunktur? På Helseklinikken i Gjerrild nær Djursland og Århus, tilbyder vi specialiserede behandlinger for blandt andet sportsskader, rygsmerter, barnløshed, stress, KOL, migræne og gigtsygdomme i led og muskler.
                    </p>
                    <p>
                      Med over 40 års erfaring er vi en af Danmarks ældste klinikker inden for akupunktur og Traditionel Kinesisk Medicin (TKM). Vores klinik er placeret i Gjerrild nær Grenaa, og inden for rækkevidde af både Aarhus og Randers.
                    </p>
                  </div>
                </div>

                <div className="space-y-4 pt-4 border-t border-stone-100">
                  <h3 className="font-sans text-lg md:text-xl font-bold text-stone-900 leading-tight">
                    Behandling tilpasset dine behov
                  </h3>
                  <p className="text-sm text-stone-600 leading-relaxed">
                    Lars og Jørgen, som er dedikerede behandlere, er ikke kun eksperter i akupunktur og TKM, men også erfarne instruktører i Chi Kung, åndedrætsteknikker, meditation og genoptræningsøvelser. De tilpasser hver behandling til den enkeltes behov og sundhedsmål, med det formål at forbedre både fysisk sundhed og mentalt velbefindende. Uanset om dit mål er at håndtere specifikke helbredsudfordringer eller simpelthen at forbedre din generelle livskvalitet, er vi her for at hjælpe dig.
                  </p>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-8">
                <div className="aspect-[16/10] overflow-hidden rounded-none shadow-xs bg-stone-100">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/557636-305391606197576-942530587-n_4.jpg"
                    alt="Lars og Jørgen inde på klinikken"
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="space-y-4">
                  <h3 className="font-sans text-lg md:text-xl font-bold text-stone-900 leading-tight">
                    En helhedsorienteret tilgang til dit velvære
                  </h3>
                  <div className="prose prose-stone text-sm text-stone-600 leading-relaxed space-y-4">
                    <p>
                      Vores tilgang til behandling fokuserer på at genskabe balance i kroppens energi og fremme harmoni mellem krop og sind. Klinikken har en dybdegående ekspertise i at identificere og behandle de bagvedliggende årsager til ubalancer og sygdomme.
                    </p>
                    <p>
                      Vi anvender en række metoder, herunder ansigts-, tunge- og pulsdiagnose, kinesiologi, cupping, akupunktur, kost og EFT for at sikre en omfattende behandling.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Full-width Contact Box */}
            <div className="space-y-4 p-6 md:p-8 bg-forest-50/40 border border-forest-200/60 mt-12 md:mt-16">
              <h3 className="font-sans text-lg md:text-xl font-bold text-stone-900 leading-tight flex items-center gap-2">
                <i className="fa-regular fa-envelope-open text-forest-800" aria-hidden="true"></i>
                Kontakt os om akupunktur og rygsmerter
              </h3>
              <p className="text-sm text-stone-600 leading-relaxed">
                Har du spørgsmål om hvad vi kan hjælpe med i forhold til akupunktur og rygsmerter på Djursland nær Århus, er du velkommen til at kontakte os på telefonnummer <a href="tel:+4586384117" className="text-forest-900 hover:underline font-semibold whitespace-nowrap">(+45) 86 38 41 17</a>, mobil <a href="tel:+4523114117" className="text-forest-900 hover:underline font-semibold whitespace-nowrap">(+45) 23 11 41 17</a> eller skrive en mail til <a href="mailto:info@helselarsen.dk" className="text-forest-900 hover:underline font-semibold">info@helselarsen.dk</a>.
              </p>
            </div>
          </div>
        </section>

        {/* 3. Treatment Grid ("Vi behandler blandt andet:") */}
        <section className="bg-stone-50/50 py-16 md:py-24 px-6 border-t border-b border-stone-200">
          <div className="max-w-6xl mx-auto">
            
            {/* Header Block */}
            <div className="text-center max-w-3xl mx-auto space-y-4 mb-12">
              <img 
                src="https://assets.auxodev.dk/cdn-cgi/image/width=240,quality=90,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/trae_1.png" 
                alt="Træ logo" 
                className="mx-auto h-24 w-auto object-contain"
              />
              <h2 className="font-sans text-2xl md:text-3xl font-bold tracking-tight text-[#181c16]">
                Vi behandler blandt andet:
              </h2>
              <p className="text-xs text-stone-500 max-w-xl mx-auto">
                Klik på en lidelse for at læse mere om vores erfaring, behandlingsforløb og metoder til netop den pågældende tilstand.
              </p>
            </div>

            {/* Four-Column Responsive Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-12 gap-y-4">
              {weTreatColumns.map((col, colIdx) => (
                <ul key={colIdx} className="space-y-2.5 list-disc pl-5 marker:text-forest-500 text-forest-700 font-medium text-xs leading-snug">
                  {col.map((item) => (
                    <li key={item.label}>
                      <a 
                        href={item.href}
                        className="hover:text-forest-900 hover:underline transition-colors duration-200"
                      >
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              ))}
            </div>

            <div className="mt-12 pt-8 text-center border-t border-stone-200/60">
              <p className="text-stone-500 text-xs">
                Du kan læse mere om de enkelte sygdomme og lidelser under menupunktet{" "}
                <a href="/sygdomme-lidelser" className="text-forest-700 font-semibold underline hover:text-forest-900">
                  "Sygdomme / lidelser"
                </a>
                .
              </p>
            </div>
          </div>
        </section>

        {/* 4. Testimonials / "Patienthistorier" Container */}
        <section className="max-w-6xl mx-auto px-6 mb-16">
          <div className="relative overflow-hidden border-2 border-dotted border-stone-300 rounded-3xl p-8 md:p-10 bg-stone-50/50">
            
            {/* Faint background image */}
            <div className="absolute inset-0 z-0 pointer-events-none select-none">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=80,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/glade-patienter-fortaeller.png"
                alt=""
                className="w-full h-full object-cover opacity-8"
              />
            </div>

            {/* Content Block */}
            <div className="relative z-10 w-full space-y-6">
              <div className="space-y-2">
                <h2 className="font-sans text-2xl md:text-3xl font-bold text-stone-900 tracking-tight">
                  Patienthistorier
                </h2>
                <p className="text-sm text-stone-600 leading-relaxed max-w-3xl">
                  Siden 1981 har masser af mennesker besøgt Helseklinikken i Gjerrild. Glade patienter har valgt at fortælle og skrive deres historie for at støtte den alternative behandling og give dig mulighed for at høre om deres erfaringer.
                </p>
              </div>
              
              {/* Horizontal slider of the latest patient stories */}
              <div className="py-2">
                <StoryMarquee stories={featuredStories} hideHeading={true} />
              </div>

              <div className="pt-2">
                <a
                  href="/patienthistorier"
                  className="inline-block bg-forest-700 text-white hover:bg-forest-800 px-6 py-2.5 text-xs font-semibold rounded-full transition-colors"
                >
                  Læs alle historier
                </a>
              </div>
            </div>

          </div>
        </section>

        {/* 5. About Section ("Om Helseklinikken i Gjerrild") */}
        <section className="py-20 md:py-24 bg-white">
          <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Left Column (5/12 width) */}
            <div className="lg:col-span-5 space-y-4">
              <div className="flex gap-2 mb-4">
                {[1, 2, 3, 4, 5].map((dot) => (
                  <span key={dot} className="h-2 w-2 rounded-full bg-forest-500" />
                ))}
              </div>
              <h2 className="font-sans text-2xl md:text-3xl font-bold text-stone-900 tracking-tight">
                Om Helseklinikken i Gjerrild
              </h2>
            </div>

            {/* Right Column (7/12 width) */}
            <div className="lg:col-span-7 space-y-6">
              <div className="prose prose-stone text-sm text-stone-600 leading-relaxed space-y-4">
                <p>
                  Helseklinikken i Gjerrild blev etableret i 1981 af Lars I. Larsen. Dengang bestod behandlingen af zoneterapi, blide manipulationsteknikker samt kostvejledning. Siden da har klinikken udvidet af flere omgange, og i 1987 fik Lars assistance af sin bror Jørgen.
                </p>
                <p>
                  Vi arbejder ud fra Traditionel Kinesisk Medicin (TKM) og har over 40 års erfaring med at hjælpe patienter med alt fra akutte sportsskader, rygsmerter og kroniske lidelser, barnløshed og stress. Vores mål er altid at genskabe kroppens naturlige balance og energi.
                </p>
              </div>
              <div className="pt-2">
                <a
                  href="/klinikkens-baggrund"
                  className="inline-block bg-forest-700 text-white hover:bg-forest-800 px-6 py-2.5 text-xs font-semibold rounded-full transition-colors"
                >
                  Om klinikken
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* 6. News / "Nyheder" Container */}
        <section className="max-w-6xl mx-auto px-6 mb-12">
          <div className="border-2 border-dotted border-stone-300 rounded-3xl p-8 md:p-10 flex flex-col md:flex-row gap-8 items-center bg-stone-50/50">
            
            {/* Left Column (Image Block) */}
            <div className="w-full md:w-2/5 aspect-[4/3] md:aspect-square overflow-hidden rounded-2xl bg-stone-100">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/nyheder.png"
                alt="Nyheder fra Helseklinikken"
                className="w-full h-full object-cover"
              />
            </div>
            
            {/* Right Column (Content Block) */}
            <div className="w-full md:w-3/5 text-left space-y-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-forest-700 italic">
                Seneste nyt fra klinikken
              </p>
              <h2 className="font-sans text-2xl md:text-3xl font-bold text-stone-900 tracking-tight">
                Nyheder
              </h2>
              <p className="text-sm text-stone-600 leading-relaxed">
                Hold dig opdateret med vores seneste nyheder, tiltag og oplysninger fra Helseklinikken i Gjerrild her og nu.
              </p>
              <div className="pt-2">
                <a
                  href="/nyheder"
                  className="inline-block bg-forest-700 text-white hover:bg-forest-800 px-6 py-2.5 text-xs font-semibold rounded-full transition-colors"
                >
                  Læs nyheder
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* 7. Chi Kung & Workshops Container (Standalone Section) */}
        <section className="max-w-6xl mx-auto px-6 mb-12">
          <div className="border-2 border-dotted border-stone-300 rounded-3xl p-8 md:p-10 flex flex-col md:flex-row-reverse gap-8 items-center bg-stone-50/50">
            
            {/* Image Block */}
            <div className="w-full md:w-2/5 aspect-[4/3] md:aspect-square overflow-hidden rounded-2xl bg-stone-100">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/img-1948-scaled-1-e1715605947279.jpg"
                alt="Zhan Zhuang Chi Kung – Stå stille og ha' det godt"
                className="w-full h-full object-cover"
              />
            </div>
            
            {/* Content Block */}
            <div className="w-full md:w-3/5 text-left space-y-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-forest-700 italic">
                Energitræning & Workshops
              </p>
              <h2 className="font-sans text-2xl md:text-3xl font-bold text-stone-900 tracking-tight">
                Zhan Zhuang Chi Kung – Stå stille og ha' det godt
              </h2>
              <p className="text-sm text-stone-600 leading-relaxed">
                Vi er erfarne instruktører i Zhan Zhuang Chi Kung – en ældgammel kinesisk form for stående meditation og energitræning, der opbygger kropslig styrke, indre ro og vitalitet. Læs mere om vores hold og kommende workshops.
              </p>
              <div className="pt-2 flex flex-wrap gap-3">
                <a
                  href="/behandlingsmetoder/chi-kung-energitraening"
                  className="inline-block bg-forest-700 text-white hover:bg-forest-800 px-6 py-2.5 text-xs font-semibold rounded-full transition-colors"
                >
                  Læs om Chi Kung
                </a>
                <a
                  href="/behandlingsmetoder/chi-kung-workshop"
                  className="inline-block bg-white text-forest-800 border border-forest-600 hover:bg-forest-50 px-6 py-2.5 text-xs font-semibold rounded-full transition-colors"
                >
                  Chi Kung Workshop
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* 8. Pre-Footer Call-to-Action Bar */}
        <section className="max-w-6xl mx-auto px-6 mb-16">
          <div className="bg-forest-50 py-10 px-8 md:px-12 rounded-3xl flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="space-y-3 text-center md:text-left">
              <div className="flex justify-center md:justify-start gap-2">
                {[1, 2, 3, 4, 5].map((dot) => (
                  <span key={dot} className="h-1.5 w-1.5 rounded-full bg-forest-500" />
                ))}
              </div>
              <h3 className="font-sans text-xl md:text-2xl font-bold text-stone-900 tracking-tight">
                Kontakt klinikken
              </h3>
              <p className="text-xs text-stone-600 max-w-md">
                Har du spørgsmål eller ønsker du at booke en tid til akupunktur eller anden behandling? Vi er altid klar til at hjælpe dig.
              </p>
            </div>
            <div className="shrink-0">
              <a
                href="/kontakt"
                className="inline-block bg-forest-700 text-white hover:bg-forest-800 px-6 py-3 text-xs font-bold uppercase tracking-wider rounded-full transition-colors"
              >
                Kontakt info
              </a>
            </div>
          </div>
        </section>

      </main>
    </>
  )
}
